<template>
  <div v-if="currentUser" class="partners-admin">
    <div class="page-header">
      <h1>Zarządzanie partnerami</h1>
      <button class="btn-primary" @click="toggleAddPanel">
        {{ showAddPanel ? 'Zamknij formularz' : '+ Dodaj partnera' }}
      </button>
    </div>

    <div class="partners-layout">
      <div class="partners-section position-relative">
        <HintComponent page-key="admin_panel_partners" :absolute="true" />
        <div class="section-card">
          <div class="section-card-header">
            <h2>Lista partnerów</h2>
          </div>

          <div class="partners-list">
            <div class="partner-card" v-for="item in partnersData" :key="item.id">
              <div class="partner-card-image">
                <img :src="item.logo_url" :alt="item.name">
              </div>
              <div class="partner-card-body">
                <h3 class="partner-title">{{ item.name }}</h3>
                <a :href="item.url" class="partner-link" target="_blank" rel="noopener noreferrer">
                  {{ item.url }}
                </a>
                <p class="partner-targets">
                  Widoczność: {{ formatTargetGroups(item.target_groups) }}
                </p>

                <div class="partner-actions">
                  <button class="btn-icon" @click="startEdit(item)" title="Edytuj">
                    <img src="@/assets/icons/pencil-square.svg" alt="edytuj">
                  </button>
                  <button class="btn-icon btn-danger" @click="deletePartner(item.id)" title="Usuń">
                    <img src="@/assets/icons/trash.svg" alt="usuń">
                  </button>
                </div>
              </div>
            </div>

            <div v-if="partnersData.length === 0" class="empty-state">
              <p>Brak partnerów do wyświetlenia.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="form-section">
        <div class="section-card" v-if="showAddPanel">
          <h2>Dodaj partnera</h2>

          <div class="form-group">
            <label for="newPartnerName">Nazwa</label>
            <input type="text" id="newPartnerName" v-model="newPartnerName" required>
          </div>

          <div class="form-group">
            <label for="newPartnerUrl">Odnośnik</label>
            <input type="text" id="newPartnerUrl" v-model="newPartnerUrl" required>
          </div>

          <div class="form-group">
            <label class="label-with-hint">
              Grupy docelowe
              <HintComponent page-key="admin_panel_partners_target_field" :absolute="false" />
            </label>
            <div class="target-groups">
              <label class="target-option" v-for="option in partnerTargetOptions" :key="option.value">
                <input
                    type="checkbox"
                    :value="option.value"
                    v-model="newPartnerTargetGroups"
                    @change="handleTargetGroupChange('new', option.value)"
                >
                <span>{{ option.label }}</span>
              </label>
            </div>
          </div>

          <div class="form-group">
            <label for="newPartnerLogo">Logo</label>
            <input
                type="file"
                accept="image/*"
                id="newPartnerLogo"
                ref="newPartnerLogo"
                @change="uploadImage"
                required
            >
          </div>

          <div class="form-actions">
            <button class="btn-green" @click="addPartner">Dodaj</button>
          </div>
        </div>

        <div class="section-card" v-if="isEdit">
          <div class="section-card-header">
            <h2>Edytuj partnera</h2>
            <button class="btn-outline" @click="cancelEdit">Anuluj</button>
          </div>

          <div class="form-group">
            <label for="editPartnerName">Nazwa</label>
            <input type="text" id="editPartnerName" v-model="editPartnerName" required>
          </div>

          <div class="form-group">
            <label for="editPartnerUrl">Odnośnik</label>
            <input type="text" id="editPartnerUrl" v-model="editPartnerUrl" required>
          </div>

          <div class="form-group">
            <label class="label-with-hint">
              Grupy docelowe
              <HintComponent page-key="admin_panel_partners_target_field" :absolute="false" />
            </label>
            <div class="target-groups">
              <label class="target-option" v-for="option in partnerTargetOptions" :key="`edit-${option.value}`">
                <input
                    type="checkbox"
                    :value="option.value"
                    v-model="editPartnerTargetGroups"
                    @change="handleTargetGroupChange('edit', option.value)"
                >
                <span>{{ option.label }}</span>
              </label>
            </div>
          </div>

          <div class="form-group">
            <label for="editPartnerLogo">Logo (opcjonalnie)</label>
            <input
                type="file"
                accept="image/*"
                id="editPartnerLogo"
                ref="editPartnerLogo"
                @change="uploadEditImage"
            >
          </div>

          <div class="form-actions">
            <button class="btn-green" @click="editPartner">Zapisz zmiany</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import authHeader from '@/services/auth-header';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import HintComponent from "@/components/SpecialComponents/HintComponent.vue";

export default {
  name: 'PartnersEdit',
  components: { HintComponent },
  setup() {
    return {
      authStore: useAuthStore(),
      appStore: useAppStore(),
    };
  },
  data() {
    return {
      showAddPanel: false,
      newPartnerName: '',
      newPartnerUrl: '',
      newPartnerLogo: null,
      newPartnerTargetGroups: ['all_users'],
      partnersData: [],
      editPartnerName: '',
      editPartnerUrl: '',
      editPartnerTargetGroups: ['all_users'],
      isEdit: false,
      editPartnerLogo: null,
      editPartnerId: null,
      partnerTargetOptions: [
        { value: 'clients', label: 'do właścicieli zwierząt' },
        { value: 'specialists', label: 'do specjalistów' }
      ],
    };
  },
  computed: {
    currentUser() {
      return this.authStore.user;
    },
  },
  mounted() {
    if (!this.currentUser) {
      this.$router.push('/login');
    } else if (this.currentUser.role_id !== 1) {
      this.$router.push('/login');
    }
    this.getPartnersData();
  },
  methods: {
    toggleAddPanel() {
      this.showAddPanel = !this.showAddPanel;
      if (!this.showAddPanel) {
        this.resetAddForm();
      }
    },
    resetAddForm() {
      this.newPartnerName = '';
      this.newPartnerUrl = '';
      this.newPartnerLogo = null;
      this.newPartnerTargetGroups = ['all_users'];
      if (this.$refs.newPartnerLogo) {
        this.$refs.newPartnerLogo.value = '';
      }
    },
    formatTargetGroups(groups) {
      const labelsMap = {
        clients: 'do właścicieli zwierząt',
        specialists: 'do specjalistów'
      };
      const values = Array.isArray(groups) && groups.length ? groups : ['all_users'];
      return values.map(function (value) {
        return labelsMap[value] || value;
      }).join(', ');
    },
    handleTargetGroupChange(mode, changedValue) {
      const key = mode === 'edit' ? 'editPartnerTargetGroups' : 'newPartnerTargetGroups';
      let selected = this[key].slice();

      if (changedValue === 'all_users' && selected.indexOf('all_users') !== -1) {
        selected = ['all_users'];
      } else if (changedValue !== 'all_users' && selected.indexOf(changedValue) !== -1) {
        selected = selected.filter(function (item) {
          return item !== 'all_users';
        });
      }

      if (!selected.length) {
        selected = ['all_users'];
      }

      this[key] = selected;
    },
    startEdit(item) {
      this.isEdit = true;
      this.editPartnerName = item.name;
      this.editPartnerUrl = item.url;
      this.editPartnerId = item.id;
      this.editPartnerLogo = null;
      this.editPartnerTargetGroups = Array.isArray(item.target_groups) && item.target_groups.length
          ? item.target_groups.slice()
          : ['all_users'];

      if (this.$refs.editPartnerLogo) {
        this.$refs.editPartnerLogo.value = '';
      }
      window.scrollTo(0, 0);
    },
    cancelEdit() {
      this.isEdit = false;
      this.editPartnerName = '';
      this.editPartnerUrl = '';
      this.editPartnerId = null;
      this.editPartnerLogo = null;
      this.editPartnerTargetGroups = ['all_users'];

      if (this.$refs.editPartnerLogo) {
        this.$refs.editPartnerLogo.value = '';
      }
    },
    uploadImage() {
      this.newPartnerLogo = this.$refs.newPartnerLogo.files.item(0);
    },
    uploadEditImage() {
      this.editPartnerLogo = this.$refs.editPartnerLogo.files.item(0);
    },
    addPartner() {
      if (!this.newPartnerLogo || !this.newPartnerName || !this.newPartnerUrl) {
        this.appStore.setIsAlert('Uzupełnij wszystkie pola i wybierz logo');
        return;
      }

      if (!this.newPartnerTargetGroups.length) {
        this.appStore.setIsAlert('Wybierz co najmniej jedną grupę docelową');
        return;
      }

      const formData = new FormData();
      formData.append('logo', this.newPartnerLogo);

      const ob = {
        name: this.newPartnerName,
        url: this.newPartnerUrl,
        logo: formData.get('logo'),
        target_groups: this.newPartnerTargetGroups
      };

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      axios.post('/Partner', ob, config)
          .then((res) => {
            if (String(res.status).charAt(0) === '2') {
              this.appStore.setIsSuccess('Pomyślnie dodano partnera');
              this.resetAddForm();
              this.showAddPanel = false;
              this.getPartnersData();
            }
          })
          .catch(() => {
            this.appStore.setIsAlert('Nie udało się dodać partnera');
          });
    },
    getPartnersData() {
      axios.post('/PartnerSearch', { page: 1, limit: 1 }, authHeader())
          .then((res) => {
            const count = res && res.data && res.data.count ? res.data.count : 0;
            return axios.post('/PartnerSearch', { page: 1, limit: count || 1 }, authHeader());
          })
          .then((res) => {
            this.partnersData = (res && res.data && res.data.items) ? res.data.items : [];
          });
    },
    deletePartner(id) {
      if (! confirm('Czy na pewno chcesz usunąć partnera?')) {
        return;
      }

      axios.delete('/Partner/' + id, authHeader())
          .then((res) => {
            if (String(res.status).charAt(0) === '2') {
              this.appStore.setIsSuccess('Usunięto partnera');
              this.getPartnersData();
            }
          })
          .catch(() => {
            this.appStore.setIsAlert('Nie udało się usunąć partnera');
          });
    },
    editPartner() {
      if (!this.editPartnerName || !this.editPartnerUrl || !this.editPartnerId) {
        this.appStore.setIsAlert('Uzupełnij nazwę i odnośnik');
        return;
      }

      if (!this.editPartnerTargetGroups.length) {
        this.appStore.setIsAlert('Wybierz co najmniej jedną grupę docelową');
        return;
      }

      const formData = new FormData();
      if (this.editPartnerLogo) {
        formData.append('editlogo', this.editPartnerLogo);
      }

      const ob = {
        id: this.editPartnerId,
        name: this.editPartnerName,
        url: this.editPartnerUrl,
        target_groups: this.editPartnerTargetGroups
      };

      if (this.editPartnerLogo) {
        ob.logo = formData.get('editlogo');
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      axios.post('/Partner', ob, config)
          .then((res) => {
            if (String(res.status).charAt(0) === '2') {
              this.appStore.setIsSuccess('Pomyślnie edytowano partnera');
              this.cancelEdit();
              this.getPartnersData();
            }
          })
          .catch(() => {
            this.appStore.setIsAlert('Nie udało się zapisać zmian partnera');
          });
    },
  },
};
</script>

<style scoped>
.partners-admin {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
  font-family: sans-serif;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
  font-size: 2.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.partners-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.partners-section {
  flex: 1 1 0;
  min-width: 0;
}

.form-section {
  width: 360px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media (max-width: 1000px) {
  .partners-layout {
    flex-direction: column;
  }

  .form-section {
    width: 100%;
  }
}

.section-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  padding: 24px;
  box-sizing: border-box;
  width: 100%;
}

.section-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.section-card h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
}

.partners-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

@media (max-width: 760px) {
  .partners-list {
    grid-template-columns: 1fr;
  }
}

.partner-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  transition: box-shadow 0.2s, border-color 0.2s;
  background: #fff;
}

.partner-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border-color: #10b981;
}

.partner-card-image {
  width: 100%;
  height: 140px;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  box-sizing: border-box;
}

.partner-card-image img {
  max-width: 100%;
  max-height: 120px;
  object-fit: contain;
  display: block;
}

.partner-card-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.partner-title {
  font-size: 1.6rem;
  color: #1f2937;
  margin: 0;
  font-weight: 700;
}

.partner-link {
  font-size: 1.3rem;
  color: #6b7280;
  text-decoration: none;
  word-break: break-all;
}

.partner-link:hover {
  color: #10b981;
}

.partner-targets {
  margin: 0;
  font-size: 1.2rem;
  color: #374151;
}

.partner-actions {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
}

.form-group label {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
}

.form-group label.label-with-hint {
  margin-bottom: 4px;
}

.form-group input[type='text'],
.form-group input[type='file'] {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 1.5rem;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  width: 100%;
}

.form-group input[type='file'] {
  padding: 8px;
  cursor: pointer;
}

.form-group input:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
}

.target-groups {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
}

.target-option {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.4rem;
  font-weight: 400;
  color: #1f2937;
}

.target-option input[type='checkbox'] {
  width: 16px;
  height: 16px;
}

.form-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-primary {
  background-color: #f97316;
  color: #fff;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background-color: #ea6c0a;
}

.btn-green {
  background-color: #10b981;
  color: #fff;
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-green:hover {
  background-color: #059669;
}

.btn-outline {
  background-color: transparent;
  color: #1f2937;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 1.5rem;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}

.btn-outline:hover {
  border-color: #10b981;
  color: #10b981;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  transition: background-color 0.2s;
}

.btn-icon:hover {
  background-color: #f3f4f6;
}

.btn-icon img {
  width: 20px;
  height: 20px;
}

.btn-danger:hover {
  background-color: #fee2e2;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 32px;
  color: #9ca3af;
  font-size: 1.5rem;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
}

@media (max-width: 480px) {
  .partners-admin {
    padding: 16px 12px;
  }

  .page-header {
    margin-bottom: 20px;
  }

  .page-header h1 {
    font-size: 2.2rem;
  }

  .section-card {
    padding: 16px 12px;
    border-radius: 8px;
  }

  .section-card h2 {
    font-size: 1.8rem;
  }

  .btn-green,
  .btn-outline {
    width: 100%;
    text-align: center;
  }
}
</style>